import UI from "@mile-meeting/mile-ui";

import { useEffect, useRef, useState } from "react";
import { ContentContainer, FormContainer, Title, Container, ReservationOptions } from "./index.styles";
import { UtilsService } from "utils/utils.service";
import { RRuleService } from "utils/rrule.service";
import {
  Title as TitleInput,
  DateInput,
  Time as TimeInput,
  MeetingRoom as MeetingRoomInput,
  Attendees as AttendeesInput,
  Memo as MemoInput,
  ReservationConfidential,
  EditPermissionCheckbox,
  RepeatReservationBtn,
  RepeatCondition,
} from "./input-forms";
import { useRecoilValue } from "recoil";
import { member, user, workspace } from "recoil/recoilState";
import { getMeetingRoomList, TMeetingRoom } from "apis/meetings/meeting-room";
import { TFormattedAttendee } from "apis/meetings/meeting-reservation";
import { useToast } from "hook/useToast";
import { LoadingModal } from "uis/modal";
import { sleep } from "utils/validation";
import OutWarningModal from "./out-warning-modal";
import useDraggableResizableModal from "hook/useDraggableResizableModal";
import { services } from "./service";
import { ReservationModalProps } from "./types";
import { TRRule } from "uis/modal/repeat-rule-setting-modal/types";
import { AmplitudeService } from "utils/amplitude.service";
import * as Modal from "uis/modal";

//* INFO: 예약하기 버튼이 활성화 될 수 있는 조건
const enableReservation = (
  title: string,
  attendees: TFormattedAttendee[],
  minAttendeeLimit: number,
  isAllowViolateMinAttendeeLimit: boolean,
  meetingRoom?: TMeetingRoom,
  isAbleReservation?: boolean
) => {
  if (isAbleReservation === false) return false;
  if (attendees.length < minAttendeeLimit && !isAllowViolateMinAttendeeLimit) return false;
  return title?.length > 0 && meetingRoom && attendees.length > 0;
};

const ReservationModal = ({
  open,
  setOpen,
  defaultDate = new Date(),
  onUpdate,
  selectedStartTime = new Date(),
  selectedEndTime,
  onChangeDate,
  onChangeTime,
  selectedMeetingRoom,
  onChangeMeetingRoom,
  requestClose,
  onChangeRequestClose,
  closeCallback,
  isAbleReservation = false,
}: ReservationModalProps) => {
  const { showToast } = useToast();
  const today = new Date();
  const utilsService = new UtilsService();
  const rruleService = new RRuleService(utilsService);
  const amplitudeService = new AmplitudeService();
  const workspaceInfo = useRecoilValue(workspace);

  const ref = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState<string>();
  const [meetingRooms, setMeetingRooms] = useState<TMeetingRoom[]>([]);
  const memberInfo = useRecoilValue(member);
  const userInfo = useRecoilValue(user);
  const [attendees, setAttendees] = useState<TFormattedAttendee[]>([]);
  const [memo, setMemo] = useState<string>();
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [openOutWarningModal, setOpenOutWarningModal] = useState(false);
  const [modalExit, setModalExit] = useState(false);
  const [initData, setInitData] = useState<any>();
  const [availableReservation, setAvailableReservation] = useState<string[]>([]);
  const [unavailableReservation, setUnavailableReservation] = useState<string[]>([]);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState<boolean>(false);
  const [showAllDuplicateAlert, setShowAllDuplicateAlert] = useState(false);
  const [rrule, setRrule] = useState<TRRule>();
  const { modalPosition, modalHeight, startDrag, startResize, setModalPosition, setModalHeight } =
    useDraggableResizableModal();
  const [isScheduleEditEnabled, setIsEditScheduleEnabled] = useState<boolean>(false);
  const [minAttendeeLimit, setMinAttendeeLimit] = useState<number>(1);
  const [isAllowViolateMinAttendeeLimit, setIsAllowViolateMinAttendeeLimit] = useState<boolean>(false);

  //* INFO : 최소 참석 제한에 막혀 예약을 하지 못하는 경우 확인
  const checkMinAttendeeLimit = () => {
    if (isAbleReservation === false) return;
    if (title && title?.length > 0 && selectedMeetingRoom && attendees.length > 0) {
      if (attendees.length < minAttendeeLimit && !isAllowViolateMinAttendeeLimit) {
        //* INFO: 최소 참석 제한에 막혀 예약을 하지 못하는 경우
        amplitudeService.logPeakTimeMinAttendanceReservationBlock(
          workspaceInfo?.id ?? "",
          workspaceInfo?.name ?? "",
          selectedMeetingRoom?.id ?? "",
          selectedMeetingRoom?.name ?? "",
          "예약",
          minAttendeeLimit,
          memberInfo?.id ?? "",
          memberInfo?.email ?? "",
          attendees.map((item) => item.id ?? "")
        );
      }
      if (attendees.length < minAttendeeLimit && isAllowViolateMinAttendeeLimit) {
        //* INFO: 최소 참석 위반 예약 생성시
        amplitudeService.logMinAttendanceExceptionReservation(
          workspaceInfo?.id ?? "",
          workspaceInfo?.name ?? "",
          selectedMeetingRoom?.id ?? "",
          selectedMeetingRoom?.name ?? "",
          "예약",
          minAttendeeLimit,
          memberInfo?.id ?? "",
          memberInfo?.email ?? "",
          attendees.map((item) => item.id ?? "")
        );
      }
    }
  };

  //* 시간, 참석자 변경 여부 체크
  const editTimeAttendeesFlag = useRef<boolean>(false);

  const onHandleReservation = async () => {
    if (loading) return;
    setLoading(true);
    if (!selectedStartTime || !selectedEndTime || !selectedMeetingRoom) return;

    if (rrule) {
      //* INFO: 반복 예약일 경우 처리 (1. 중복 체크)
      const duplicated_list = await services.checkRecurringReservationDuplicate(
        selectedStartTime,
        selectedEndTime,
        selectedMeetingRoom,
        rruleService.generateRRuleString(rrule, selectedStartTime),
        defaultDate
      );

      if (duplicated_list && duplicated_list?.available.length === 0 && duplicated_list?.unavailable.length > 0) {
        //* INFO: 모든 예약이 중복 예약일 경우
        setShowAllDuplicateAlert(true);
        setLoading(false);
        return;
      } else if (duplicated_list && duplicated_list?.unavailable.length > 0) {
        //* INFO: 중복 예약이 1개라도 있을 경우
        setAvailableReservation(duplicated_list.available);
        setUnavailableReservation(duplicated_list.unavailable);
        setShowDuplicateAlert(true);
        setLoading(false);
        return;
      } else {
        //* INFO: 중복 예약이 없을 경우
        await reservation(duplicated_list?.available.length ?? 1);
        setLoading(false);
        return;
      }
    } else {
      //* INFO: 단일 예약일 경우 처리 (바로 예약 진행)
      await reservation(1);
      setLoading(false);
      return;
    }
  };

  const reservation = async (totalReservationCount: number) => {
    if (!selectedStartTime || !selectedEndTime || !selectedMeetingRoom) return;

    const res = await services.reservation(
      defaultDate,
      selectedStartTime,
      selectedEndTime,
      title ?? "",
      isPrivate,
      isScheduleEditEnabled,
      selectedMeetingRoom,
      attendees,
      memo,
      rrule ? rruleService.generateRRuleString(rrule, defaultDate) : undefined,
      isAllowViolateMinAttendeeLimit
    );
    if (res.success) {
      if (selectedMeetingRoom?.meetingRoomIntegration?.integrationProvider === "GOOGLE") {
        await sleep(4000);
      }
      onUpdate?.();
      showToast(`${totalReservationCount}건의 "${title}" 예약에 성공했습니다.`, "success");
      setOpen?.(false);
    } else {
      showToast(
        res.message ?? "회의실 예약에 실패하였습니다. 다시 시도해주세요.",
        res.status === 409 ? "warning" : "error"
      );
    }
  };

  useEffect(() => {
    if (open && memberInfo) {
      //* 회의실 목록 조회
      getMeetingRoomList(1, [], [], 50).then((res) => {
        setMeetingRooms(res.data?.meetingRoomList ?? []);
      });

      //* 초기 데이터 설정
      const { title, attendees, date, memo, isPrivate, guestsCanModify } = services.getInitInfo(
        memberInfo,
        defaultDate
      );
      setTitle(title);
      setAttendees(attendees);
      setMemo(memo);
      setIsPrivate(isPrivate);
      setIsEditScheduleEnabled(guestsCanModify);
      setRrule(undefined);
      onChangeDate?.(date);
      onChangeTime?.(
        services.getInitStartTime(date, selectedStartTime),
        services.getInitEndTime(date, selectedStartTime)
      );
      setModalExit(false);
      setInitData({
        title,
        attendees,
        date,
        memo,
        isPrivate,
      });
      setIsAllowViolateMinAttendeeLimit(false);

      //* 모달 위치 설정
      editTimeAttendeesFlag.current = false;
      setModalPosition({ x: window.innerWidth - 460, y: 88 });
      setModalHeight(window.innerHeight - 88 - 20);
    }
  }, [open]);

  useEffect(() => {
    if (selectedStartTime === undefined)
      onChangeTime?.(
        services.getInitStartTime(defaultDate, selectedStartTime),
        services.getInitEndTime(defaultDate, selectedStartTime)
      );
  }, [selectedStartTime]);

  useEffect(() => {
    if (
      requestClose &&
      services.checkChanges(
        initData,
        title ?? "",
        editTimeAttendeesFlag.current,
        isPrivate,
        isScheduleEditEnabled,
        selectedMeetingRoom ? true : false,
        memo
      )
    ) {
      setOpenOutWarningModal(true);
    } else if (requestClose) {
      setOpen?.(false);
      closeCallback?.();
      onChangeRequestClose?.(false);
    }
  }, [requestClose]);

  useEffect(() => {
    setTitle?.(`${memberInfo?.nickname}의 회의실 예약`);
    setAttendees([
      {
        isVisitor: false,
        id: memberInfo?.id,
        email: userInfo?.email ?? "",
        picture: memberInfo?.image,
        name: memberInfo?.nickname,
      },
    ]);
  }, [memberInfo?.nickname]);

  useEffect(() => {
    if (selectedMeetingRoom && selectedStartTime && selectedEndTime) {
      const limit = services.getAttendeeLimit(
        selectedMeetingRoom?.attendeeLimits ?? [],
        selectedStartTime,
        selectedEndTime
      );
      setMinAttendeeLimit(limit);
    }
  }, [selectedMeetingRoom, selectedStartTime, selectedEndTime]);

  return (
    <Container
      style={{
        display: open ? "flex" : "none",
        left: `${modalPosition.x}px`,
        top: `${modalPosition.y}px`,
        height: modalHeight,
      }}
      ref={ref}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          height: 40,
          left: 0,
          width: "100%",
          display: "flex",
          cursor: "move",
          padding: "0 20px",
          boxSizing: "border-box",
          justifyContent: "end",
          alignItems: "end",
        }}
        onMouseDown={startDrag}
      >
        <UI.IconButton.Normal
          type="primary"
          icon="x"
          size={20}
          color="var(--alias-color-label-normal)"
          onClick={() => {
            if (
              services.checkChanges(
                initData,
                title ?? "",
                editTimeAttendeesFlag.current,
                isPrivate,
                isScheduleEditEnabled,
                selectedMeetingRoom ? true : false,
                memo
              )
            ) {
              setOpenOutWarningModal(true);
              setModalExit(true);
            } else setOpen?.(false);
          }}
        />

        <div
          style={{
            position: "absolute",
            top: 12,
            left: 12,
          }}
        >
          <UI.Icon name="move" size={20} color="var(--alias-color-label-assistive)" />
        </div>
      </div>
      <Title>
        <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-neutral)">
          회의실 예약
        </UI.Typography>
      </Title>
      <div className="scroll-point">
        <ContentContainer>
          <FormContainer>
            <TitleInput onChange={setTitle} value={title} />
            <div className="date-input" style={{ display: "flex", gap: 8, flexDirection: "column", width: "100%" }}>
              <DateInput
                value={defaultDate}
                onChange={(value) => {
                  onChangeDate?.(value);

                  if (editTimeAttendeesFlag.current === false) editTimeAttendeesFlag.current = true;
                }}
              />
              <div style={{ display: "flex", gap: 8, width: "100%" }}>
                <TimeInput
                  isToday={defaultDate ? new Date(defaultDate).toDateString() === new Date().toDateString() : false}
                  startValue={
                    selectedStartTime
                      ? { hour: selectedStartTime?.getHours() ?? 0, minutes: selectedStartTime?.getMinutes() ?? 0 }
                      : undefined
                  }
                  onChangeStart={(hour, minutes) => {
                    onChangeTime?.(
                      new Date(defaultDate.setHours(hour, minutes)),
                      new Date(
                        defaultDate.setHours(selectedEndTime?.getHours() ?? 0, selectedEndTime?.getMinutes() ?? 0)
                      )
                    );

                    editTimeAttendeesFlag.current = true;
                  }}
                  endValue={
                    selectedEndTime
                      ? { hour: selectedEndTime?.getHours() ?? 0, minutes: selectedEndTime?.getMinutes() ?? 0 }
                      : undefined
                  }
                  onChangeEnd={(hour, minutes) => {
                    onChangeTime?.(
                      new Date(
                        defaultDate.setHours(selectedStartTime?.getHours() ?? 0, selectedStartTime?.getMinutes() ?? 0)
                      ),
                      new Date(defaultDate.setHours(hour, minutes))
                    );

                    editTimeAttendeesFlag.current = true;
                  }}
                />
                <RepeatReservationBtn
                  selectedDate={selectedStartTime}
                  onChange={setRrule}
                  isShow={rrule === undefined}
                />
              </div>
            </div>
            <RepeatCondition rrule={rrule} onChange={setRrule} selectedDate={selectedStartTime} />
            <MeetingRoomInput
              open={open}
              meetingRooms={meetingRooms}
              onChange={(value) => {
                onChangeMeetingRoom?.(value);
              }}
              value={selectedMeetingRoom}
              attendeeCount={attendees.length}
            />
            <AttendeesInput
              open={open}
              value={attendees}
              onChange={(value) => {
                setAttendees(value);
                if (editTimeAttendeesFlag.current === false) editTimeAttendeesFlag.current = true;
              }}
              maxCapacity={selectedMeetingRoom?.capacity}
              minAttendeeLimit={minAttendeeLimit}
              isAllowViolateMinAttendeeLimit={isAllowViolateMinAttendeeLimit}
              onChangeAllowViolateMinAttendeeLimit={() => setIsAllowViolateMinAttendeeLimit(true)}
            />
          </FormContainer>
          <MemoInput value={memo ?? ""} onChange={setMemo} />
        </ContentContainer>
        <ReservationOptions>
          <ReservationConfidential value={isPrivate} onChange={setIsPrivate} />
          <EditPermissionCheckbox value={isScheduleEditEnabled} onChange={setIsEditScheduleEnabled} />
        </ReservationOptions>
      </div>
      <UI.Button
        type="solid"
        title="예약하기"
        disabled={
          !enableReservation(
            title ?? "",
            attendees,
            selectedEndTime
              ? services.getAttendeeLimit(selectedMeetingRoom?.attendeeLimits ?? [], selectedStartTime, selectedEndTime)
              : 1,
            isAllowViolateMinAttendeeLimit,
            selectedMeetingRoom,
            isAbleReservation
          )
        }
        size="large"
        style={{ padding: "12px 0", margin: "0 40px" }}
        onClick={() => {
          if (loading) return;
          //* INFO: 최소 참석 제한 관련 로그
          checkMinAttendeeLimit();
          const enable = enableReservation(
            title ?? "",
            attendees,
            selectedEndTime
              ? services.getAttendeeLimit(selectedMeetingRoom?.attendeeLimits ?? [], selectedStartTime, selectedEndTime)
              : 1,
            isAllowViolateMinAttendeeLimit,
            selectedMeetingRoom,
            isAbleReservation
          );

          if (enable) onHandleReservation();
        }}
      />
      <div className="resize-down" onMouseDown={startResize} />
      <OutWarningModal
        modalIsOpen={openOutWarningModal}
        closeModal={() => {
          setOpenOutWarningModal(false);
          onChangeRequestClose?.(false);
        }}
        onChange={() => {
          setOpenOutWarningModal(false);
          setOpen?.(false);
          if (!modalExit) closeCallback?.();
          onChangeRequestClose?.(false);
        }}
      />
      <Modal.ReservationAllConflictModal
        modalOpen={showAllDuplicateAlert}
        closeModal={() => setShowAllDuplicateAlert(false)}
      />
      <Modal.ReservationConflictModal
        modalIsOpen={showDuplicateAlert}
        closeModal={() => setShowDuplicateAlert(false)}
        totalReservationCount={availableReservation.length + unavailableReservation.length}
        enableReservationCount={availableReservation.length}
        disableReservationCount={unavailableReservation.length}
        enableReservationTimes={unavailableReservation.map((item) => new Date(item))}
        onReservation={async () => {
          setShowDuplicateAlert(false);
          setLoading(true);
          await reservation(availableReservation.length);
          setLoading(false);
        }}
      />
      <LoadingModal modalIsOpen={loading} />
    </Container>
  );
};

export default ReservationModal;
